import { object as ST, string, optional, size, intersection } from 'superstruct';
import { fileList, message, required, requiredDependsOn, weekday } from '@/utils/inputValidation';

import { useUnifiedApiPostSupport } from './services/postSupport';
import { buildSupportRfiFormData } from './utils/supportRfiFormData';

import type { OnSubmitFunc } from '@/types/form';
import type { SupportFormFields, SupportResponse, SupportType } from './types';
import type { FetchError } from '@/types/api';

interface UseSupportFormProps {
  onSuccess?: (data: SupportResponse) => void;
  onError?: (error: FetchError) => void;
  type?: SupportType;
  relationship?: string;
  extraDescription?: string;
}

export const useSupportForm = ({
  onSuccess,
  onError,
  relationship,
  extraDescription,
}: UseSupportFormProps) => {
  const { isPending: isPostingUnifiedApiRfi, mutate: sendUnifiedApiEmail } =
    useUnifiedApiPostSupport();

  const onSubmitUnifiedApi: OnSubmitFunc<SupportFormFields> = (
    rawFormData,
    setGeneralError,
    setSuccessMessage
  ) => {
    const relationshipData = relationship ? { [relationship]: [+rawFormData.ticket_id] } : {};
    const subClientData =
      rawFormData.subclient_id && rawFormData.subclient_id !== 'Myself'
        ? { clients: [+rawFormData.subclient_id] }
        : {};

    const getDescription = () => `
        ${rawFormData.description}
        ${rawFormData.extraDescription && extraDescription ? `${extraDescription} ${rawFormData.extraDescription}` : ''}
        `;

    const formData = buildSupportRfiFormData(
      {
        name: rawFormData.subject,
        description: getDescription(),
        due_at: rawFormData.due_date,
        ...relationshipData,
        ...subClientData,
      },
      rawFormData.attachment ? [...rawFormData.attachment] : undefined
    );

    sendUnifiedApiEmail(formData, {
      onSuccess: (data) => {
        setSuccessMessage('Request sent successfully');
        onSuccess?.(data);
      },
      onError: (error) => {
        setGeneralError({ ...error, message: error?.responseError });
        onError?.({ ...error, message: error?.responseError });
      },
    });
  };

  const schema = ST({
    subject: required(string(), 'Subject is required'),
    otherSubject: requiredDependsOn('subject', 'Other'),
    description: message(
      size(string(), 3, Infinity),
      'Description should have at least 3 characters'
    ),
    // TODO: Make extraDescription validation more extensible to allow more versatiliy
    extraDescription: optional(string()),
    due_date: intersection([
      required(string(), 'Due Date is required'),
      weekday('Weekend dates are not allowed as a deadline'),
    ]),
    attachment: optional(fileList()),
    to: required(string(), ''),
    ticket_id: optional(string()),
    subclient_id: optional(string()),
  });

  return {
    isPostingRfi: isPostingUnifiedApiRfi,
    schema,
    onSubmit: onSubmitUnifiedApi,
    sendEmail: sendUnifiedApiEmail,
  };
};
